import { EASE_OUT_CUBIC, OVERLAY_DURATION } from '@/helpers/timing'
import { useBodyClip } from '@/hooks/useBodyClip'
import { motion, type HTMLMotionProps, type Variants } from 'motion/react'
import type { FC, PropsWithChildren } from 'react'
import { useEffect } from 'react'
import { Corridor } from '../atoms/Corridor'
import { Grid } from '../atoms/Grid'
import { IconCircle } from '../atoms/IconCircle'
import { Tag } from '../atoms/Tag'

export type OverlayProps = Omit<HTMLMotionProps<'div'>, 'title' | 'children'> & {
	titleTag: string
	open: boolean
	setOpen: (open: boolean) => void
}

export const overlayWrapperVariants: Variants = {
	hidden: { display: 'none', transitionTimingFunction: 'step-end' },
	visible: { display: 'flex', transitionTimingFunction: 'step-start' },
}

export const bgOverlayVariants: Variants = {
	hidden: {
		opacity: 0,
		transition: { duration: OVERLAY_DURATION, ease: EASE_OUT_CUBIC },
	},
	visible: { opacity: 0.9, transition: { duration: OVERLAY_DURATION, ease: EASE_OUT_CUBIC } },
}

const contentVariants: Variants = {
	hidden: { opacity: 0, y: 20, transition: { duration: OVERLAY_DURATION, ease: EASE_OUT_CUBIC } },
	visible: {
		opacity: 1,
		y: 0,
		transition: { duration: OVERLAY_DURATION, ease: EASE_OUT_CUBIC },
	},
}

export const Overlay: FC<PropsWithChildren<OverlayProps>> = ({
	id,
	titleTag,
	open,
	setOpen,
	children,
	...props
}) => {
	// Handle Escape key with higher priority and body overflow
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				event.preventDefault()
				event.stopImmediatePropagation()
				setOpen(false)
			}
		}
		if (open) {
			// Use capture phase to intercept before other handlers
			document.addEventListener('keydown', handleKeyDown, true)
			return () => document.removeEventListener('keydown', handleKeyDown, true)
		}
	}, [open, setOpen])

	// Clip body when overlay is open
	useBodyClip(open, `overlay-${id}`)

	return (
		<motion.div
			id={id}
			className="fixed top-0 left-0 z-100 h-full w-full flex-col items-end justify-end hidden"
			role="dialog"
			aria-modal="true"
			variants={overlayWrapperVariants}
			initial="hidden"
			animate={open ? 'visible' : 'hidden'}
			{...props}
		>
			<motion.div
				variants={bgOverlayVariants}
				className="absolute top-0 left-0 h-full w-full bg-blue-shade-70 pointer-events-none"
			/>
			<motion.div
				variants={contentVariants}
				className="flex w-full justify-end flex-col h-full relative"
			>
				{/* Close button and translucent header */}
				<Corridor className="h-overlay-header-height flex items-end py-xl justify-end">
					<button
						id={`${id}-close`}
						className="flex w-fit cursor-pointer items-center gap-x-md text-button text-white group"
						onClick={() => setOpen(false)}
						aria-controls={id}
						aria-expanded={open}
					>
						<span>Close</span>
						<IconCircle icon={['fas', 'times']} theme="blue" size="sm" />
					</button>
				</Corridor>
				{/* Overlay content */}
				<div className="w-full bg-white h-[calc(100vh-var(--spacing-overlay-header-height))] overflow-y-auto">
					<Corridor className="pt-6xl pb-7xl">
						<Grid>
							<div className="col-span-full tablet-l:col-span-8 tablet-l:col-start-3">
								<Tag label={titleTag} intent="blue-tint-10" />
								<div className="mt-lg">{children}</div>
							</div>
						</Grid>
					</Corridor>
				</div>
			</motion.div>
		</motion.div>
	)
}

export default Overlay
